import React, {
  useState,
  useEffect,
  useRef,
} from "react";

import {
  Form,
  Input,
  Button,
  Table,
  message,
  Modal,
} from "antd";

import { RecLimitedApiService } from "../ApiService/RecApiService";

const RecReceipts = () => {
  const [form] = Form.useForm();

  const [messageApi, contextHolder] =
    message.useMessage();

  const [loading, setLoading] =
    useState(false);

  const [otpLoading, setOtpLoading] =
    useState(false);

  const [resendBlocked, setResendBlocked] =
    useState(false);

  const [confirmBlocked, setConfirmBlocked] =
    useState(false);

  const [transactions, setTransactions] =
    useState([]);

  const [searched, setSearched] =
    useState(false);

  // ---------------- OTP Modal ----------------

  const [otpModalOpen, setOtpModalOpen] =
    useState(false);

  const [pendingValues, setPendingValues] =
    useState(null);

  const [otp, setOtp] = useState([
    "",
    "",
    "",
    "",
  ]);

  const otpRefs = useRef([]);

  const [timer, setTimer] = useState(30);

  const startTimer = () => {
    setTimer(30);

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return interval;
  };

  const timerRef = useRef(null);

  useEffect(() => {
    if (!otpModalOpen) return;

    otpRefs.current[0]?.focus();

    timerRef.current = startTimer();

    return () => {
      clearInterval(timerRef.current);
    };
  }, [otpModalOpen]);

  const handleOtpChange = (
    value,
    index
  ) => {
    if (!/^\d?$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;

    setOtp(updatedOtp);

    if (value && index < 3) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    e,
    index
  ) => {
    if (
      (e.ctrlKey || e.metaKey) &&
      ["c", "v", "x"].includes(e.key.toLowerCase())
    ) {
      e.preventDefault();
      return;
    }

    if (
      e.key === "Backspace" &&
      !otp[index] &&
      index > 0
    ) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpCancel = () => {
    setOtpModalOpen(false);
    setResendBlocked(false);
    setConfirmBlocked(false);
    setOtp(["", "", "", ""]);
  };

  const handleOtpConfirm = async () => {
    if (otp.join("").length !== 4) {
      messageApi.error("Please enter the OTP");
      return;
    }

    try {
      setOtpLoading(true);
      const verifyResponse = await RecLimitedApiService.verifyOtp({
        mobile_number: pendingValues.mobile,
        otp: otp.join(""),
      });

      if (!verifyResponse?.status) {
        messageApi.error(
          verifyResponse?.message || "OTP verification failed. Please try again."
        );
        setOtp(["", "", "", ""]);
        if (verifyResponse?.message?.toLowerCase().includes("blocked")) {
          setConfirmBlocked(true);
        }
        return;
      }

      setOtpModalOpen(false);
      handleSearch(pendingValues.mobile, verifyResponse.session_token);
    } catch {
      messageApi.error("OTP verification failed. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleFormSubmit = async (
    values
  ) => {
    try {
      setLoading(true);
      const response = await RecLimitedApiService.sendOtp({
        mobile_number: values.mobile,
      });

      if (!response?.status) {
        messageApi.error(
          response?.message ||
            "No records exist for this mobile number."
        );
        return;
      }

      setPendingValues(values);
      setOtp(["", "", "", ""]);
      setResendBlocked(false);
      setConfirmBlocked(false);
      setOtpModalOpen(true);
    } catch {
      messageApi.error("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Existing Search ----------------

  const handleSearch = async (mobile, sessionToken) => {
    try {
      setLoading(true);

      const response =
        await RecLimitedApiService.getTransactions({
          mobile_number: mobile,
          session_token: sessionToken,
        });

      if (!response?.status) {
        messageApi.error(
          response?.message ||
            response?.data?.message ||
            "Unable to fetch transactions."
        );
        setTransactions([]);
        setSearched(true);
        return;
      }

      const transactionData =
        response.data.transactions.map((item, index) => ({
          id: index + 1,
          fees_category: "Payment Collection",
          txn_date: item.transaction_complete_date,
          txn_number: item.third_party_transaction_id,
          payment_mode: "Netbanking",
          amount: item.transaction_amount,
          txnid: item.third_party_transaction_id,
          mobile_number: mobile,
          ebz_pay_id: item.ebz_pay_id,
          transaction_complete_date: item.transaction_complete_date,
        }));

      setTransactions(transactionData);
      setSearched(true);
    } catch (error) {
      messageApi.error("Unable to fetch transactions.");
      setTransactions([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

    // ---------------- Download Receipt ----------------

  const handleDownloadReceipt = async (
    record
  ) => {
    try {
      const queryData = {
        txnid: record.txnid,
        mobile_number:
          record.mobile_number,
      };

      const response =
        await RecLimitedApiService.downloadReceipt(
          queryData,
          {
            responseType: "blob",
          }
        );

      const fileType =
        "application/pdf";

      if (
        fileType !==
        "application/pdf"
      ) {
        console.error(
          "⚠️ Invalid response type received:",
          fileType
        );
        return;
      }

      const blob = new Blob(
        [response],
        {
          type: "application/pdf",
        }
      );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const a =
        document.createElement("a");

      a.href = url;

      a.download = `REC${record.ebz_pay_id}${record.transaction_complete_date}.pdf`;

      a.click();

      window.URL.revokeObjectURL(
        url
      );
    } catch (error) {
      messageApi.error(
        "Unable to open receipt."
      );
    }
  };

  const totalAmount =
    transactions.reduce(
      (sum, item) =>
        sum +
        Number(item.amount || 0),
      0
    );

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
    },
    {
      title: "Fees Category",
      dataIndex:
        "fees_category",
      render: (_, record) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() =>
            handleDownloadReceipt(
              record
            )
          }
        >
          Payment Collection
        </Button>
      ),
    },
    {
      title: "Txn Date",
      dataIndex: "txn_date",
    },
    {
      title: "Txn Number",
      dataIndex:
        "txn_number",
    },
    {
      title:
        "Payment Mode",
      dataIndex:
        "payment_mode",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      align: "right",
      render: (value) =>
        `₹${Number(
          value
        ).toLocaleString(
          "en-IN",
          {
            minimumFractionDigits: 2,
          }
        )}`,
    },
  ];

  return (
    <>
      {contextHolder}

      <div className="rec-receipts-container">
        <div className="rec-receipts-title">
          Enter Mobile Number for
          Previous Receipts
        </div>

        <Form
          form={form}
          onFinish={
            handleFormSubmit
          }
        >
          <div className="rec-search-row">
            <Form.Item
              name="mobile"
              className="rec-search-item"
              rules={[
                {
                  required: true,
                  message:
                    "Please enter mobile number",
                },
                {
                  pattern:
                    /^[6-9]\d{9}$/,
                  message:
                    "Please enter valid mobile number",
                },
              ]}
            >
              <Input
                placeholder="Mobile Number"
                maxLength={10}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="rec-primary-btn"
            >
              View Previous
              Receipts
            </Button>
          </div>
        </Form>

        {searched &&
          transactions.length >
            0 && (
            <>
              <Table
                rowKey="id"
                columns={
                  columns
                }
                dataSource={
                  transactions
                }
                size="small"
                pagination={
                  transactions.length >
                  20
                    ? {
                        pageSize: 20,
                        showSizeChanger: false,
                      }
                    : false
                }
              />

              <div className="rec-table-total">
                <span>
                  Total
                  Transactions:{" "}
                  {
                    transactions.length
                  }
                </span>

                <span>
                  Total Amount:
                  ₹
                  {totalAmount.toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                    }
                  )}
                </span>
              </div>
            </>
          )}

                  <Modal
          open={otpModalOpen}
          footer={null}
          onCancel={handleOtpCancel}
          centered
          width={420}
          destroyOnClose
        >
          <div
            style={{
              fontSize: 22,
              fontWeight: 600,
              marginBottom: 32,
              textAlign: "center",
            }}
          >
            Verify Mobile No.
          </div>

          <div
            style={{
              marginBottom: 16,
              fontWeight: 500,
            }}
          >
            Enter OTP{" "}
            <span style={{ color: "red" }}>
              *
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              marginBottom: 28,
            }}
          >
            {otp.map(
              (digit, index) => (
                <Input
                  key={index}
                  ref={(el) =>
                    (otpRefs.current[
                      index
                    ] = el)
                  }
                  value={digit}
                  maxLength={1}
                  onChange={(e) =>
                    handleOtpChange(
                      e.target.value,
                      index
                    )
                  }
                  onKeyDown={(e) =>
                    handleOtpKeyDown(
                      e,
                      index
                    )
                  }
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  style={{
                    width: 55,
                    height: 55,
                    textAlign:
                      "center",
                    fontSize: 22,
                    borderRadius: 8,
                  }}
                />
              )
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 32,
            }}
          >
            {!resendBlocked && (timer > 0 ? (
              <span style={{ color: "#666" }}>
                Resend OTP in{" "}
                <strong>{timer}</strong>{" "}
                seconds
              </span>
            ) : (
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={async () => {
                  const res = await RecLimitedApiService.resendOtp({
                    mobile_number: pendingValues?.mobile,
                  });
                  if (!res?.status) {
                    messageApi.error(res?.message || "Failed to resend OTP.");
                    if (
                      res?.message?.toLowerCase().includes("blocked") ||
                      res?.message?.toLowerCase().includes("too many")
                    ) {
                      setResendBlocked(true);
                    }
                    return;
                  }
                  setOtp(["", "", "", ""]);
                  clearInterval(timerRef.current);
                  timerRef.current = startTimer();
                }}
              >
                Resend OTP
              </Button>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent:
                "flex-end",
              gap: 10,
            }}
          >
            <Button
              style={{
                minWidth: 95,
                height: 38,
              }}
              onClick={
                handleOtpCancel
              }
            >
              Cancel
            </Button>

            <Button
              type="primary"
              style={{
                minWidth: 95,
                height: 38,
              }}
              disabled={confirmBlocked}
              loading={otpLoading}
              onClick={handleOtpConfirm}
            >
              Confirm
            </Button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default RecReceipts;